import React, { useMemo, useState } from "react"
import { useParams, useHistory } from "react-router"

import { SvgBackgroundBox } from "components/marketing/svg-background-box"
import Pagination from "components/pagination"
import Grid from "components/grid"

import { PostSummary } from "./post-summary"

import { useMountedEffect } from "hooks/useMountedEffect"
import { useScrollToTop } from "hooks/useScrollToTop"

import { useRedirect } from "../hooks/useRedirect"
import { usePages } from "../hooks/usePosts"

import theme from "theme"

export const PAGINATION_LIMIT = 8

const Blob = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="2332" height="2707" viewBox="0 0 2332 2707">
    <g fill={theme.colors.iceBlue}>
      <path d="M1084.604 1834.777c145.658 126.785 228.412 451.916 145.346 596.108-83.339 144.246-332.77 107.607-506.758 88.765-174.26-18.788-273.556-19.325-411.748-125.199C172.911 2288.283-4.723 2077.18 23.88 1969.182c28.262-108.291 263.17-112.833 484.701-151.206 221.531-38.372 430.092-109.932 576.023 16.8zM1922.786 331.533c200.66 314.096 367.9 1058.409 299.004 1363.314-69.211 304.966-374.872 170.586-586.67 91.866-212.112-78.66-330.837-100.746-518.181-366.518C929.126 1153.693 672.532 644.357 683.984 408.92c10.983-236.166 290.633-196.912 547.189-236.106 256.555-39.194 490.637-155.315 691.613 158.72z" />
    </g>
  </svg>
)

const BlobMobile = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="375" height="1012" viewBox="0 0 375 1012">
    <path
      fill={theme.colors.iceBlue}
      d="M540.982 114.963C682.389 294.4 784.243 729.836 722.005 912.767c-62.479 182.977-289.29 113.497-446.93 73.881-157.879-39.57-246.975-48.718-379.91-199.967C-238.1 635.015-415.353 341.76-398.88 201.891c16.14-140.285 226.427-126.301 421.84-158.03 195.414-31.729 376.375-108.289 518.023 71.102z"
    />
  </svg>
)

export type Params = { path: string; page: string | undefined; section: string }

export const PostSummaryList: React.FC = () => {
  const history = useHistory()
  const { path, page, section } = useParams<Params>()
  const { pages, posts } = usePages()

  const pageFromSection = Number(section)
  const pageFromUrl = pageFromSection || Number(page)

  const [currentPage, setCurrentPage] = useState(pageFromUrl > pages ? 1 : pageFromUrl || 1)

  const postUrl = useMemo(() => !!posts.map(({ path }) => path).find(path => path === page), [page])

  useRedirect(pages, postUrl)
  useScrollToTop([currentPage])

  useMountedEffect(() => {
    if (pageFromSection || page) {
      setCurrentPage(pageFromUrl || 1)
      return
    }
  }, [page, pageFromSection])

  // support 2 version of pagination. If user pass anything in params, we change page in url, if no, we just change current page
  const handleChange = (newPage: number) => {
    if (page || pageFromSection) {
      history.push(path ? `/learn/section/${path}/${newPage}` : `/learn/${newPage}`)
      return
    }

    setCurrentPage(newPage)
  }

  const startElement = currentPage * PAGINATION_LIMIT - PAGINATION_LIMIT
  const stopElement = currentPage * PAGINATION_LIMIT

  return (
    <>
      <SvgBackgroundBox svg={[BlobMobile, BlobMobile, Blob, Blob]} backgroundSize={["100%", null, "cover"]}>
        {posts.slice(startElement, stopElement).map((post, idx: number) => (
          <Grid
            key={post.path}
            rowProps={{ justifyContent: "center" }}
            mb={idx !== posts.length - 1 ? [6, null, 9] : undefined}
          >
            <PostSummary {...post} path={`/learn/post/${post.path}`} />
          </Grid>
        ))}
      </SvgBackgroundBox>
      <Grid rowProps={{ justifyContent: "center" }} my={[6, 6, 8, 8]}>
        <Pagination
          onNextClick={() => handleChange(currentPage + 1)}
          onPrevClick={() => handleChange(currentPage - 1)}
          onChange={handleChange}
          currentPage={currentPage}
          pagesCount={pages}
        />
      </Grid>
    </>
  )
}
